import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { AxiosError } from 'axios';
import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  listEmployees,
  deleteEmployee,
  getEmployee,
  employeeUpdate,
  employeeCreate,
  getEmployeeContacts,
  postEmployeeContacts,
  removeEmployeeContacts,
  detailEmployeeContacts,
  putEmployeeContacts,
  getEmployeeAddress,
  postEmployeeAddress,
  putEmployeeAddress,
  removeEmployeeAddress,
  removeEmployeeContactDetail,
  detailEmployeeProfessional,
  createEmployeeProfessional,
  editEmployeeProfessional,
  listEmployeesReduced,
  getEmployeeReduced,
  getEmployeeInfo,
  getEmployeeIsEstimator,
  grantAccessToEmployee,
} from './Employees.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '../../people';
import {
  Employee,
  EmployeeAddress,
  EmployeeEditFormValues,
  EmployeeIsEstimator,
  EmployeeReduced,
  ListEmployeesQuery,
  ListEmployeesQueryReduced,
} from './Employees.types';
import notificationError from '@/utils/functions/notificationError';
import { EmployeesErrors } from './Employees.errors';
import useSession from '@/hooks/useSession';
import { queryClient, store } from '@/Root';
import { authOperations } from '@/state/ducks/auth';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useEmployees(params?: ListEmployeesQuery, enable?: boolean) {
  const query = useInfiniteQuery(
    ['employees', { ...params, enable: !!enable }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listEmployees(req);
    },
    {
      enabled: typeof enable === 'boolean' ? enable : true,
      cacheTime: 0,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useEmployeesReduced(
  params?: ListEmployeesQueryReduced,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['employees/reduced', { ...params }]);

  return useInfiniteQuery(
    ['employees/reduced', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listEmployeesReduced(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      cacheTime: 10000,
      onError: () => {
        notification.error({
          message: 'Erro na listagem',
          description: `Ocorreu um erro ao tentar listar os ${
            params?.estimator ? 'Orçamentistas' : 'Funcionários'
          }.`,
        });
      },
    },
  );
}

export function useEmployee(id?: number) {
  return useQuery(['employee', id], () => (id ? getEmployee(id) : null), {
    enabled: !!id,
    onError: (error) => {
      notificationError(error);
    },
  });
}

export function useEmployeeInfo(id?: number) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['employeeInfo', id]);

  return useQuery(
    ['employeeInfo', id],
    () => (id ? getEmployeeInfo(id) : null),
    {
      enabled: !!id && !cache,
      cacheTime: 10000,
      onError: (error) => {
        notificationError(error);
      },
    },
  );
}

export function useEmployeeIsEstimatorMutate() {
  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<EmployeeIsEstimator>([
        'isEstimator',
        id,
      ]);

      if (!!cache) return cache;

      return getEmployeeIsEstimator(id);
    },
    {
      onSuccess(data, id) {
        queryClient.setQueryData(['isEstimator', id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useEmployeeDetail() {
  return useMutation(getEmployee, {
    onError(error) {
      notificationError(error);
    },
  });
}

export function useEmployeeDetailReduced(id?: number) {
  const cache = queryClient.getQueryData(['employee-detail-reduced', id]);

  return useQuery(
    ['employee-detail-reduced', id],
    () => (id ? getEmployeeReduced(id) : null),
    {
      enabled: !!id && !cache,
      onError: (error) => {
        notificationError(error);
      },
    },
  );
}

export function useEmployeeDetailReducedMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<EmployeeReduced>([
        'employee-detail-reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      const data = await getEmployeeReduced(id);
      return data;
    },
    {
      onError(error) {
        notificationError(error);
      },
      onSuccess(data) {
        queryClient.setQueryData(['employee-detail-reduced', data.id], data);
      },
    },
  );
}

export function useEmployeeCreate() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(employeeCreate, {
    onSuccess() {
      queryClient.refetchQueries(['employee']);

      notification.success({
        message: intl.formatMessage({
          id: 'employee.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'employee.create.notification.successDescription',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeUpdate() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();
  const session = useSession();

  const mutation = useMutation<Employee, AxiosError, EmployeeEditFormValues>(
    employeeUpdate,
    {
      async onSuccess(data) {
        if (session && session.employeeId === data.id) {
          const sessionCompanyIds = session.companyIds;
          const dataCompanies = data.companies;

          if (
            sessionCompanyIds.every((companyId) =>
              dataCompanies.some((company) => company.id === companyId),
            ) &&
            session.companyIds.length === data.companies.length
          ) {
            await store.dispatch(authOperations.handleRefreshToken());
          }
        }

        notification.success({
          message: intl.formatMessage({
            id: 'employee.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'employee.update.notification.successDescription',
          }),
        });

        queryChanel({
          resetQueries: [['employeeInfo', data.id]],
          refetchQueries: [['employee']],
          removeQueries: [
            ['isEstimator', data.id],
            ['employee-detail-reduced', data.id],
          ],
        });
      },
    },
  );

  return mutation;
}

export function useDeleteEmployee() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteEmployee(id), {
    onSuccess() {
      queryClient.refetchQueries(['employees']);

      notification.success({
        message: intl.formatMessage({
          id: 'employee.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'employee.remove.notification.successDescription',
        }),
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            EmployeesErrors[err] ||
            err.includes('Employee Referential integrity constraint violation')
              ? 'Não foi possível excluir o funcionário. Existem vinculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useEmployeeContactList(employeeId?: number) {
  const query = useQuery(['employee/contacts', employeeId], () =>
    !!employeeId ? getEmployeeContacts(employeeId) : [],
  );

  useServiceError(query.error);

  return query;
}

export function useEmployeeContactCreate() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(postEmployeeContacts, {
    onSuccess() {
      queryClient.invalidateQueries('employee/contacts');

      notification.success({
        message: intl({ id: 'commons.modal.contact.added.message' }),
        description: intl({
          id: 'commons.modal.contact.added.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeContactEdit() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(putEmployeeContacts, {
    onSuccess() {
      queryClient.invalidateQueries('employee/contacts');

      notification.success({
        message: intl({ id: 'commons.modal.contact.updated.message' }),
        description: intl({
          id: 'commons.modal.contact.updated.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeContactDetail() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { employeeId: number; contactId: number }) =>
      detailEmployeeContacts(data.employeeId, data.contactId),
    {
      onSuccess() {
        queryClient.invalidateQueries('employee/contacts');

        notification.success({
          message: intl({ id: 'commons.modal.contact.updated.message' }),
          description: intl({
            id: 'commons.modal.contact.updated.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeContactRemove() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { employeeId: number; contactId: number }) =>
      removeEmployeeContacts(data.employeeId, data.contactId),
    {
      onSuccess() {
        queryClient.invalidateQueries('employee/contacts');

        notification.success({
          message: intl({ id: 'commons.modal.contact.removed.message' }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeContactDetailRemove() {
  const intl = useIntl().formatMessage;

  const mutation = useMutation(
    (data: { employeeId: number; detailId: number }) =>
      removeEmployeeContactDetail(data.employeeId, data.detailId),
    {
      onSuccess() {
        notification.success({
          message: intl({ id: 'commons.modal.contact.removed.message' }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeAddress(employeeId?: number) {
  const query = useQuery(
    ['employee/address', employeeId],
    () => getEmployeeAddress(employeeId),
    {
      enabled: !!employeeId,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useEmployeeAddressCreate() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: EmployeeAddress & { employeeId: number }) =>
      postEmployeeAddress(data),
    {
      onSuccess() {
        queryClient.invalidateQueries('employee/address');

        notification.success({
          message: intl({ id: 'commons.modal.address.added.message' }),
          description: intl({
            id: 'commons.modal.address.added.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeAddressEdit() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: EmployeeAddress & { employeeId: number }) =>
      putEmployeeAddress(data),
    {
      onSuccess() {
        queryClient.invalidateQueries('employee/address');

        notification.success({
          message: intl({ id: 'commons.modal.address.updated.message' }),
          description: intl({
            id: 'commons.modal.address.updated.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeAddressRemove() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data: { employeeId: number; addressId: number }) =>
      removeEmployeeAddress(data.employeeId, data.addressId),
    {
      onSuccess() {
        queryClient.invalidateQueries('employee/address');

        notification.success({
          message: intl({ id: 'commons.modal.address.removed.message' }),
          description: intl({
            id: 'commons.modal.address.removed.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeProfessional(employeeId?: number) {
  const query = useQuery(
    ['employee/professional', employeeId],
    () => (employeeId ? detailEmployeeProfessional(employeeId) : null),
    {
      enabled: !!employeeId,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useEmployeeProfessionalCreate() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(createEmployeeProfessional, {
    onSuccess() {
      queryClient.invalidateQueries('employee/professional');

      notification.success({
        message: intl({ id: 'employee.professional.success.message' }),
        description: intl({
          id: 'employee.professional.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeProfessionalEdit() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(editEmployeeProfessional, {
    onSuccess() {
      queryClient.invalidateQueries('employee/professional');

      notification.success({
        message: intl({ id: 'employee.professional.success.message' }),
        description: intl({
          id: 'employee.professional.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useEmployeeGrantAccessYardManagement() {
  const queryClient = useQueryClient();

  return useMutation(grantAccessToEmployee, {
    onSuccess(_, employeeId) {
      queryClient.invalidateQueries(['employee', employeeId]);

      notification.success({
        message: 'Sucesso!',
        description:
          'Acesso concedido ao funcionário para acessar a Gestão de Pátio!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
